import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, Asset, Location } from '../lib/api';
import { Plus, Search, X, MapPin, Tag, Package, ChevronRight, QrCode, AlertCircle, ChevronDown, Check, Wrench, Settings, Truck, Calendar, Euro, CalendarClock, Clock, AlertTriangle } from 'lucide-react';
import { DataTable, Column, Toolbar, ResultsCount } from '../components/DataTable';
import { useViewMode } from '../contexts/ViewModeContext';

const statusConfig: Record<string, { label: string; className: string }> = {
  active: { label: 'Attivo', className: 'badge-green' },
  in_repair: { label: 'In Riparazione', className: 'badge-orange' },
  in_maintenance: { label: 'In Manutenzione', className: 'badge-blue' },
  in_transit: { label: 'In Transito', className: 'badge-purple' },
  in_storage: { label: 'In Magazzino', className: 'badge-gray' },
  disposed: { label: 'Dismesso', className: 'badge-red' },
  lost: { label: 'Perso', className: 'badge-red' }
};

const conditionConfig: Record<string, { label: string; className: string }> = {
  excellent: { label: 'Eccellente', className: 'text-emerald-600' },
  good: { label: 'Buono', className: 'text-blue-600' },
  fair: { label: 'Discreto', className: 'text-yellow-600' },
  poor: { label: 'Scarso', className: 'text-orange-600' },
  broken: { label: 'Rotto', className: 'text-red-600' }
};

// Format currency in euros
function formatCurrency(cents: number | null | undefined): string {
  if (!cents) return '-';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

// Format date in Italian locale
function formatDate(timestamp: number | string | null | undefined): string {
  if (!timestamp) return '-';
  const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);
  return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Multi-select location filter component with search
function LocationMultiSelect({ 
  locations, 
  selected, 
  onChange 
}: { 
  locations: Location[]; 
  selected: string[]; 
  onChange: (ids: string[]) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredLocations = locations.filter(loc => 
    loc.name.toLowerCase().includes(search.toLowerCase())
  );

  const toggleLocation = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter(s => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const selectedNames = selected
    .map(id => locations.find(l => l.id === id)?.name)
    .filter(Boolean)
    .slice(0, 2);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="input input-sm w-full text-left flex items-center justify-between gap-2"
      >
        <span className="truncate text-sm">
          {selected.length === 0 ? (
            <span className="text-gray-500">Ubicazione</span>
          ) : selected.length === 1 ? (
            selectedNames[0]
          ) : (
            `${selectedNames.join(', ')}${selected.length > 2 ? ` +${selected.length - 2}` : ''}`
          )}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca ubicazione..."
                className="input input-sm pl-7 w-full"
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-48">
            {filteredLocations.length === 0 ? (
              <div className="p-3 text-sm text-gray-500 text-center">Nessuna ubicazione trovata</div>
            ) : (
              filteredLocations.map((loc) => (
                <button
                  key={loc.id}
                  type="button"
                  onClick={() => toggleLocation(loc.id)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                    selected.includes(loc.id) 
                      ? 'bg-primary-500 border-primary-500 text-white' 
                      : 'border-gray-300'
                  }`}>
                    {selected.includes(loc.id) && <Check className="w-3 h-3" />}
                  </div>
                  <span className="text-sm text-gray-700 truncate">{loc.name}</span>
                </button>
              ))
            )}
          </div>
          {selected.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Deseleziona tutto
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Assets() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useViewMode();
  
  // Get initial values from URL
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || '');
  const [locationFilter, setLocationFilter] = useState<string[]>(() => {
    const loc = searchParams.get('location');
    return loc ? loc.split(',').filter(Boolean) : [];
  });

  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (statusFilter) params.set('status', statusFilter);
  if (categoryFilter) params.set('category', categoryFilter);
  if (locationFilter.length > 0) params.set('location', locationFilter.join(','));

  const { data, isLoading, error } = useQuery({
    queryKey: ['assets', search, statusFilter, categoryFilter, locationFilter],
    queryFn: () => api.getAssets(params),
    refetchOnMount: 'always'
  });

  // Update URL when filters change
  const updateFilters = (newFilters: { status?: string; category?: string; locations?: string[]; search?: string }) => {
    const newParams = new URLSearchParams();
    const newStatus = newFilters.status !== undefined ? newFilters.status : statusFilter;
    const newCategory = newFilters.category !== undefined ? newFilters.category : categoryFilter;
    const newLocations = newFilters.locations !== undefined ? newFilters.locations : locationFilter;
    const newSearch = newFilters.search !== undefined ? newFilters.search : search;
    
    if (newSearch) newParams.set('search', newSearch);
    if (newStatus) newParams.set('status', newStatus);
    if (newCategory) newParams.set('category', newCategory);
    if (newLocations.length > 0) newParams.set('location', newLocations.join(','));
    
    setSearchParams(newParams);
    if (newFilters.status !== undefined) setStatusFilter(newFilters.status);
    if (newFilters.category !== undefined) setCategoryFilter(newFilters.category);
    if (newFilters.locations !== undefined) setLocationFilter(newFilters.locations);
    if (newFilters.search !== undefined) setSearch(newFilters.search);
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setCategoryFilter('');
    setLocationFilter([]);
    setSearchParams(new URLSearchParams());
  };

  const hasFilters = search || statusFilter || categoryFilter || locationFilter.length > 0;

  if (error) {
    return (
      <div className="card border-red-200 bg-red-50">
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm font-medium">Errore: {error.message}</span>
        </div>
      </div>
    );
  }

  const assets = data?.assets || [];
  const categories = data?.categories || [];
  const locations = data?.locations || [];

  // Count by status for quick stats
  const activeCount = assets.filter(a => a.status === 'active').length;
  const inRepairCount = assets.filter(a => a.status === 'in_repair').length;
  const inMaintenanceCount = assets.filter(a => a.status === 'in_maintenance').length;
  const inTransitCount = assets.filter(a => a.status === 'in_transit').length;

  // Quick stat card component
  const StatCard = ({ 
    label, 
    value, 
    icon: Icon, 
    color, 
    filterStatus
  }: { 
    label: string; 
    value: number; 
    icon: React.ElementType; 
    color: string;
    filterStatus: string;
  }) => {
    const isActive = statusFilter === filterStatus;
    
    const colorClasses: Record<string, string> = {
      emerald: 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border-emerald-200',
      purple: 'text-purple-600 bg-purple-50 hover:bg-purple-100 border-purple-200',
      orange: 'text-orange-600 bg-orange-50 hover:bg-orange-100 border-orange-200',
      blue: 'text-blue-600 bg-blue-50 hover:bg-blue-100 border-blue-200',
    };

    return (
      <button
        onClick={() => updateFilters({ status: isActive ? '' : filterStatus })}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all cursor-pointer ${colorClasses[color]} ${isActive ? 'ring-2 ring-offset-1 ring-current scale-105' : ''}`}
      >
        <Icon className="w-4 h-4" />
        <div className="text-left">
          <span className="text-lg font-semibold">{value}</span>
          <span className="text-xs ml-1 opacity-75">{label}</span>
        </div>
      </button>
    );
  };

  const columns: Column<Asset>[] = [
    {
      key: 'qrCode',
      header: 'Codice',
      width: '100px',
      sortable: true,
      render: (asset) => (
        <div className="flex items-center gap-1.5 text-xs font-mono text-gray-600">
          <QrCode className="w-3 h-3" />
          <span>{asset.qrCode}</span>
        </div>
      )
    },
    {
      key: 'name',
      header: 'Nome',
      sortable: true,
      render: (asset) => (
        <div className="font-medium text-gray-900">{asset.name}</div>
      )
    },
    {
      key: 'status',
      header: 'Stato',
      width: '120px',
      sortable: true,
      render: (asset) => {
        const s = statusConfig[asset.status] || statusConfig.active;
        return <span className={`badge ${s.className}`}>{s.label}</span>;
      }
    },
    {
      key: 'condition',
      header: 'Condizione',
      width: '90px',
      sortable: true,
      hideOnMobile: true,
      render: (asset) => {
        const c = conditionConfig[asset.condition] || conditionConfig.good;
        return <span className={`text-xs font-medium ${c.className}`}>{c.label}</span>;
      }
    },
    {
      key: 'category',
      header: 'Categoria',
      sortable: true,
      hideOnMobile: true,
      render: (asset) => asset.category ? (
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <Tag className="w-3 h-3" />
          <span className="truncate max-w-[100px]">{asset.category.name}</span>
        </div>
      ) : <span className="text-gray-400">-</span>
    },
    {
      key: 'location',
      header: 'Ubicazione',
      sortable: true,
      hideOnMobile: true,
      render: (asset) => asset.location ? (
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <MapPin className="w-3 h-3" />
          <span className="truncate max-w-[120px]">{asset.location.name}</span>
        </div>
      ) : <span className="text-gray-400">-</span>
    },
    {
      key: 'purchaseDate',
      header: 'Data Acquisto',
      width: '110px',
      sortable: true,
      hideOnMobile: true,
      render: (asset) => (
        <span className="text-xs text-gray-600">{formatDate(asset.purchaseDate)}</span>
      )
    },
    {
      key: 'purchasePrice',
      header: 'Costo Acquisto',
      width: '110px',
      sortable: true,
      hideOnMobile: true,
      render: (asset) => (
        <span className="text-xs text-gray-600 font-medium">{formatCurrency(asset.purchasePrice)}</span>
      )
    },
    {
      key: 'totalMaintenanceCost',
      header: 'Costo Interventi',
      width: '120px',
      sortable: true,
      hideOnMobile: true,
      render: (asset) => {
        // Show red only if maintenance cost >= purchase price
        const showWarning = asset.totalMaintenanceCost > 0 && 
          asset.purchasePrice && 
          asset.totalMaintenanceCost >= asset.purchasePrice;
        return (
          <span className={`text-xs font-medium ${showWarning ? 'text-red-600' : asset.totalMaintenanceCost > 0 ? 'text-gray-600' : 'text-gray-400'}`}>
            {formatCurrency(asset.totalMaintenanceCost)}
          </span>
        );
      }
    },
    {
      key: 'nextMaintenanceDate',
      header: 'Pross. Manut.',
      width: '110px',
      sortable: true,
      hideOnMobile: true,
      render: (asset) => {
        if (!asset.nextMaintenanceDate) {
          return <span className="text-gray-400 text-xs">-</span>;
        }
        
        const now = Date.now();
        const daysUntil = Math.ceil((asset.nextMaintenanceDate - now) / (1000 * 60 * 60 * 24));
        const isOverdue = daysUntil < 0;
        const isUrgent = daysUntil >= 0 && daysUntil <= 7;
        
        return (
          <div className={`flex items-center gap-1 text-xs font-medium ${
            isOverdue ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-gray-600'
          }`}>
            {isOverdue && <AlertTriangle className="w-3 h-3" />}
            {isUrgent && !isOverdue && <Clock className="w-3 h-3" />}
            {!isOverdue && !isUrgent && <CalendarClock className="w-3 h-3 text-gray-400" />}
            <span>{formatDate(asset.nextMaintenanceDate)}</span>
          </div>
        );
      }
    },
    {
      key: 'actions',
      header: '',
      width: '60px',
      render: (asset) => (
        <div className="flex items-center justify-end gap-2">
          {asset.hasIncompleteAction && (
            <AlertTriangle className="w-4 h-4 text-amber-500" />
          )}
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </div>
      )
    }
  ];

  // Mobile card render
  const mobileCardRender = (asset: Asset) => {
    const s = statusConfig[asset.status] || statusConfig.active;
    const c = conditionConfig[asset.condition] || conditionConfig.good;
    const hasCosts = asset.purchasePrice || asset.totalMaintenanceCost > 0;
    
    // Calculate next maintenance status
    let maintenanceStatus: 'overdue' | 'urgent' | 'normal' | null = null;
    if (asset.nextMaintenanceDate) {
      const daysUntil = Math.ceil((asset.nextMaintenanceDate - Date.now()) / (1000 * 60 * 60 * 24));
      maintenanceStatus = daysUntil < 0 ? 'overdue' : daysUntil <= 7 ? 'urgent' : 'normal';
    }

    // Show red cost only when >= purchase price
    const showCostWarning = asset.totalMaintenanceCost > 0 && 
      asset.purchasePrice && 
      asset.totalMaintenanceCost >= asset.purchasePrice;
    
    return (
      <div className={`mobile-card ${asset.hasIncompleteAction ? 'bg-orange-50 border-orange-200' : ''}`}>
        <div className="mobile-card-header">
          <div className="min-w-0 flex-1">
            <div className="mobile-card-title">{asset.name}</div>
            <div className="text-xs font-mono text-gray-500">{asset.qrCode}</div>
          </div>
          <span className={`badge ${s.className}`}>{s.label}</span>
        </div>
        <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
          <div className="flex items-center gap-3">
            {asset.category && (
              <span className="flex items-center gap-1">
                <Tag className="w-3 h-3" />
                {asset.category.name}
              </span>
            )}
            {asset.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {asset.location.name}
              </span>
            )}
          </div>
          <span className={`font-medium ${c.className}`}>{c.label}</span>
        </div>
        {(hasCosts || asset.nextMaintenanceDate) && (
          <div className="flex items-center gap-4 text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100 flex-wrap">
            {asset.purchasePrice && (
              <span className="flex items-center gap-1">
                <Euro className="w-3 h-3" />
                Acquisto: <span className="font-medium text-gray-700">{formatCurrency(asset.purchasePrice)}</span>
              </span>
            )}
            {asset.totalMaintenanceCost > 0 && (
              <span className="flex items-center gap-1">
                <Wrench className="w-3 h-3" />
                Interventi: <span className={`font-medium ${showCostWarning ? 'text-red-600' : 'text-gray-600'}`}>{formatCurrency(asset.totalMaintenanceCost)}</span>
              </span>
            )}
            {asset.nextMaintenanceDate && (
              <span className={`flex items-center gap-1 ${
                maintenanceStatus === 'overdue' ? 'text-red-600' : 
                maintenanceStatus === 'urgent' ? 'text-amber-600' : ''
              }`}>
                {maintenanceStatus === 'overdue' && <AlertTriangle className="w-3 h-3" />}
                {maintenanceStatus === 'urgent' && <Clock className="w-3 h-3" />}
                {maintenanceStatus === 'normal' && <CalendarClock className="w-3 h-3" />}
                Manut: <span className="font-medium">{formatDate(asset.nextMaintenanceDate)}</span>
              </span>
            )}
            {asset.hasIncompleteAction && (
              <span className="ml-auto">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
              </span>
            )}
          </div>
        )}
        {!hasCosts && !asset.nextMaintenanceDate && asset.hasIncompleteAction && (
          <div className="flex justify-end mt-2 pt-2 border-t border-gray-100">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Asset</h1>
          <p className="page-subtitle">{assets.length} asset nel campeggio</p>
        </div>
        <Link to="/assets/new" className="btn btn-primary hidden lg:flex">
          <Plus className="w-4 h-4" />
          Nuovo Asset
        </Link>
      </div>

      {/* Quick Stats - Clickable */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 sm:overflow-visible sm:pb-0 sm:mx-0 sm:px-0 sm:justify-center">
        <StatCard 
          label="Attivi" 
          value={activeCount} 
          icon={Package} 
          color="emerald"
          filterStatus="active"
        />
        <StatCard 
          label="In Transito" 
          value={inTransitCount} 
          icon={Truck} 
          color="purple"
          filterStatus="in_transit"
        />
        <StatCard 
          label="In Riparaz." 
          value={inRepairCount} 
          icon={Wrench} 
          color="orange"
          filterStatus="in_repair"
        />
        <StatCard 
          label="In Manut." 
          value={inMaintenanceCount} 
          icon={Settings} 
          color="blue"
          filterStatus="in_maintenance"
        />
      </div>

      {/* Filters Toolbar */}
      <div className="card card-sm space-y-2">
        {/* Desktop: all on one row, Mobile: search + status on first row */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          {/* Search and Status on same row on mobile */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1 sm:w-56 sm:flex-none">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => updateFilters({ search: e.target.value })}
                placeholder="Cerca asset..."
                className="input input-sm pl-8 w-full"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => updateFilters({ status: e.target.value })}
              className="input input-sm w-auto min-w-0 flex-shrink sm:flex-1 sm:min-w-[80px]"
            >
              <option value="">Stato</option>
              {Object.entries(statusConfig).map(([value, config]) => (
                <option key={value} value={value}>{config.label}</option>
              ))}
            </select>
          </div>
          
          {/* Other filters row */}
          <div className="flex items-center gap-2 flex-1 flex-wrap sm:flex-nowrap">
            <select
              value={categoryFilter}
              onChange={(e) => updateFilters({ category: e.target.value })}
              className="input input-sm w-auto min-w-0 flex-shrink sm:flex-1 sm:min-w-[80px]"
            >
              <option value="">Categoria</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            
            <div className="flex-1 min-w-[100px] sm:min-w-[120px]">
              <LocationMultiSelect
                locations={locations}
                selected={locationFilter}
                onChange={(ids) => updateFilters({ locations: ids })}
              />
            </div>
          </div>
        </div>
        
        {/* Results count on right with reset button */}
        <div className="flex items-center justify-end gap-2 text-xs text-gray-500 pt-1 border-t border-gray-100">
          {hasFilters && (
            <button onClick={clearFilters} className="btn btn-ghost btn-xs flex items-center gap-1 text-primary-600 hover:text-primary-700">
              <X className="w-3 h-3" />
              Reset
            </button>
          )}
          <ResultsCount count={assets.length} label="asset" />
        </div>
      </div>

      {/* Data Table */}
      <div className="card card-sm">
        <DataTable
          data={assets}
          columns={columns}
          keyExtractor={(a) => a.id}
          onRowClick={(a) => navigate(`/assets/${a.id}`)}
          rowClassName={(a) => a.hasIncompleteAction ? 'bg-orange-50' : ''}
          isLoading={isLoading}
          emptyMessage={hasFilters ? 'Nessun asset corrisponde ai filtri' : 'Nessun asset'}
          emptyIcon={<Package className="w-8 h-8" />}
          mobileCardRender={mobileCardRender}
        />
      </div>

    </div>
  );
}
